import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, TextField, Tooltip, Typography, useMediaQuery,
} from "@mui/material";
import { Close, Delete, Edit, Star, StarBorder, ToggleOff, ToggleOn, WhatsApp } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography, pageHeader } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import { whatsappMessagesService, WhatsAppMessageTemplate, WhatsAppMessageVariable } from "../services/whatsappMessagesService";

/**
 * Mensagens de WhatsApp (RN-545): modelos com variáveis {{chave}} que a Agenda do dia usa para montar
 * o link wa.me. Uma é a padrão da entidade. Só Administrador. O catálogo de variáveis e a prévia vêm
 * do servidor — a mesma renderização que o envio usa.
 */
interface FormState {
  id: string | null;
  name: string;
  body: string;
  metaTemplateName: string;
}

const apiError = (err: unknown, fallback: string) => {
  const d = (err as { response?: { data?: { description?: string } } })?.response?.data;
  return d?.description || fallback;
};

const WhatsAppMessages: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();
  const entityId = user?.entityId || "";

  const [rows, setRows] = useState<WhatsAppMessageTemplate[]>([]);
  const [variables, setVariables] = useState<WhatsAppMessageVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"active" | "inactive" | "all">("active");
  const [form, setForm] = useState<FormState | null>(null);
  const [preview, setPreview] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<WhatsAppMessageTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  // Abaixo de 768px a lista vira cartão próprio (padrão do sistema)
  const isMobile = useMediaQuery("(max-width:768px)");

  const load = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const [list, vars] = await Promise.all([whatsappMessagesService.getByEntity(entityId, true), whatsappMessagesService.variables()]);
      setRows(list);
      setVariables(vars);
    } catch {
      showToast("Erro ao carregar as mensagens.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Mensagens de WhatsApp";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // Prévia no servidor, com atraso curto para não bater na API a cada tecla
  useEffect(() => {
    if (!form) return;
    const body = form.body;
    if (!body.trim()) { setPreview(""); return; }
    const t = setTimeout(async () => {
      try { setPreview(await whatsappMessagesService.preview(body)); } catch (e) { setPreview(apiError(e, "Prévia indisponível.")); }
    }, 350);
    return () => clearTimeout(t);
  }, [form?.body, form]);

  const normalize = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const filtered = useMemo(() => rows.filter((r) => {
    if (status === "active" && !r.active) return false;
    if (status === "inactive" && r.active) return false;
    const term = normalize(search.trim());
    return !term || normalize(r.name).includes(term) || normalize(r.body).includes(term);
  }), [rows, search, status]);
  const hasFilters = search !== "" || status !== "active";
  const clearFilters = () => { setSearch(""); setStatus("active"); };

  const openNew = () => { setPreview(""); setForm({ id: null, name: "", body: "", metaTemplateName: "" }); };
  const openEdit = (r: WhatsAppMessageTemplate) => { setPreview(""); setForm({ id: r.id, name: r.name, body: r.body, metaTemplateName: r.metaTemplateName || "" }); };

  const insertVariable = (key: string) => {
    if (!form) return;
    const el = bodyRef.current;
    const token = `{{${key}}}`;
    const start = el?.selectionStart ?? form.body.length;
    const end = el?.selectionEnd ?? form.body.length;
    const body = form.body.slice(0, start) + token + form.body.slice(end);
    setForm({ ...form, body });
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(start + token.length, start + token.length); } });
  };

  const run = async (fn: () => Promise<void>, ok: string, fallback: string) => {
    setSaving(true);
    try { await fn(); showToast(ok, "success"); await load(); return true; } catch (e) { showToast(apiError(e, fallback), "error"); return false; } finally { setSaving(false); }
  };

  const save = async () => {
    if (!form) return;
    const payload = { name: form.name.trim(), body: form.body.trim(), metaTemplateName: form.metaTemplateName.trim() || null };
    const ok = await run(
      () => (form.id ? whatsappMessagesService.update(form.id, payload) : whatsappMessagesService.create(entityId, payload).then(() => undefined)),
      form.id ? "Mensagem atualizada." : "Mensagem incluída.",
      "Não foi possível salvar a mensagem.",
    );
    if (ok) setForm(null);
  };

  const handleRevalidateLogin = () => {
    localStorage.removeItem("clinic4us-user-session");
    localStorage.removeItem("clinic4us-remember-me");
    window.location.href = "/?page=login";
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal showCTAButton={false} className="login-header" isLoggedIn={true} notificationCount={0} onRevalidateLogin={handleRevalidateLogin} onLogoClick={goToDashboard} />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Mensagens de WhatsApp</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Modelos que a Agenda do dia usa para montar a mensagem ao paciente. Variáveis entre chaves duplas são preenchidas com os dados do agendamento.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
              <AddButton onClick={openNew} title="Nova mensagem" />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField size="small" label="Buscar" placeholder="Nome ou trecho do texto" value={search} onChange={(e) => setSearch(e.target.value)} inputProps={{ maxLength: 100 }} InputLabelProps={{ shrink: true }} sx={searchFieldSx} />
                <TextField select size="small" label="Situação" value={status} onChange={(e) => setStatus(e.target.value as "active" | "inactive" | "all")} InputLabelProps={{ shrink: true }} sx={filterFieldSx}>
                  <MenuItem value="active">Ativas</MenuItem>
                  <MenuItem value="inactive">Inativas</MenuItem>
                  <MenuItem value="all">Todas</MenuItem>
                </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters || loading} />
              </div>
            </div>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} sx={{ color: colors.primary }} /></Box>
            ) : (
              <>
                <ResultCount shown={filtered.length} total={rows.length} />
                {isMobile ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {filtered.length === 0 && (
                      <Typography sx={{ color: colors.textSecondary, fontSize: "0.875rem", py: 3, textAlign: "center" }}>Nenhuma mensagem.</Typography>
                    )}
                    {filtered.map((r) => (
                      <Box key={r.id} sx={{ border: `1px solid ${colors.borderLight}`, borderRadius: "10px", p: 1.5, background: "#fff", opacity: r.active ? 1 : 0.7 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                          <Box sx={{ fontWeight: 600, flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{r.name}</Box>
                          {r.isDefault && <Chip size="small" label="Padrão" sx={{ bgcolor: colors.successSoft, color: "#155724", fontWeight: 600, height: 20 }} />}
                          {!r.active && <Chip size="small" label="Inativa" sx={{ bgcolor: colors.backgroundAlt, color: colors.textSecondary, fontWeight: 600, height: 20 }} />}
                        </Box>
                        <Box sx={{ fontSize: "0.85rem", color: colors.textSecondary, mt: 0.5, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>{r.body.length > 160 ? `${r.body.slice(0, 160)}…` : r.body}</Box>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.75, mt: 1 }}>
                          <IconButton size="small" disabled={!r.active || saving} onClick={() => run(() => whatsappMessagesService.setDefault(r.id, !r.isDefault), r.isDefault ? "Padrão removida." : "Mensagem padrão definida.", "Não foi possível alterar a padrão.")} aria-label="Padrão"
                            sx={{ bgcolor: r.isDefault ? "#ffc107" : "#adb5bd", color: "white", width: 36, height: 36 }}>
                            {r.isDefault ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" onClick={() => openEdit(r)} aria-label="Editar" sx={{ bgcolor: colors.primary, color: "white", width: 36, height: 36 }}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" disabled={saving} onClick={() => run(() => whatsappMessagesService.setActive(r.id, !r.active), r.active ? "Mensagem inativada." : "Mensagem reativada.", "Não foi possível alterar a situação.")} aria-label="Ativar ou inativar"
                            sx={{ bgcolor: r.active ? "#6c757d" : "#4caf50", color: "white", width: 36, height: 36 }}>
                            {r.active ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" onClick={() => setDeleteTarget(r)} aria-label="Excluir" sx={{ bgcolor: colors.error, color: "white", width: 36, height: 36 }}><Delete fontSize="small" /></IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                <Box className="c4u-table">
                  <Box className="c4u-table__header">
                    <Box className="admin-plans-header-cell" sx={{ flex: "1 1 200px", minWidth: 160 }}>Nome</Box>
                    <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "3 1 320px", minWidth: 200 }}>Texto</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 110px", textAlign: "center" }}>Situação</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 170px", marginLeft: "auto", textAlign: "right" }}>Ações</Box>
                  </Box>
                  {filtered.length === 0 && (
                    <Typography sx={{ color: colors.textSecondary, fontSize: "0.875rem", py: 3, textAlign: "center" }}>
                      Nenhuma mensagem. Clique em "Nova mensagem" para criar a primeira; ela já nasce como padrão.
                    </Typography>
                  )}
                  {filtered.map((r) => (
                    <Box key={r.id} className="c4u-table__row" sx={{ opacity: r.active ? 1 : 0.6 }}>
                      <Box className="admin-plans-cell" data-label="Nome" sx={{ flex: "1 1 200px", minWidth: 160, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Box sx={{ fontWeight: 600 }}>{r.name}</Box>
                        {r.isDefault && <Chip size="small" label="Padrão" sx={{ bgcolor: colors.successSoft, color: "#155724", fontWeight: 600, height: 20 }} />}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--md" data-label="Texto" sx={{ flex: "3 1 320px", minWidth: 200, fontSize: "0.85rem", color: colors.textSecondary, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
                        {r.body.length > 220 ? `${r.body.slice(0, 220)}…` : r.body}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--lg" data-label="Situação" sx={{ flex: "0 0 110px", textAlign: "center" }}>
                        <Chip size="small" label={r.active ? "Ativa" : "Inativa"} sx={{ fontWeight: 600, bgcolor: r.active ? colors.successSoft : colors.backgroundAlt, color: r.active ? "#155724" : colors.textSecondary }} />
                      </Box>
                      <Box className="admin-plans-cell" data-label="Ações" sx={{ flex: "0 0 170px", marginLeft: "auto", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        <Tooltip title={r.isDefault ? "Deixar de ser a padrão" : "Tornar a mensagem padrão"}>
                          <span>
                            <IconButton className="c4u-row-action" size="small" disabled={!r.active || saving} onClick={() => run(() => whatsappMessagesService.setDefault(r.id, !r.isDefault), r.isDefault ? "Padrão removida." : "Mensagem padrão definida.", "Não foi possível alterar a padrão.")}
                              sx={{ bgcolor: r.isDefault ? "#ffc107" : "#adb5bd", color: "white", "&:hover": { bgcolor: r.isDefault ? "#e0a800" : "#868e96" } }}>
                              {r.isDefault ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton className="c4u-row-action" size="small" onClick={() => openEdit(r)} sx={{ bgcolor: colors.primary, color: "white", "&:hover": { bgcolor: colors.primaryHover } }}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={r.active ? "Inativar" : "Reativar"}>
                          <IconButton className="c4u-row-action" size="small" disabled={saving} onClick={() => run(() => whatsappMessagesService.setActive(r.id, !r.active), r.active ? "Mensagem inativada." : "Mensagem reativada.", "Não foi possível alterar a situação.")}
                            sx={{ bgcolor: r.active ? "#6c757d" : "#4caf50", color: "white", "&:hover": { bgcolor: r.active ? "#5a6268" : "#388e3c" } }}>
                            {r.active ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir (mensagem já usada em envio só é inativada)">
                          <IconButton className="c4u-row-action" size="small" onClick={() => setDeleteTarget(r)} sx={{ bgcolor: colors.error, color: "white", "&:hover": { bgcolor: "#c82333" } }}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
                )}
              </>
            )}
          </div>
        </Container>
      </Box>

      <Dialog open={form !== null} onClose={() => !saving && setForm(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1.25rem 1.5rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0, display: "flex", alignItems: "center", gap: 1 }}>
            <WhatsApp /> {form?.id ? "Editar mensagem" : "Nova mensagem"}
          </Typography>
          <IconButton onClick={() => setForm(null)} sx={{ color: colors.white, padding: "0.25rem" }} aria-label="Fechar"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {form && (
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" } }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField label="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth size="small" inputProps={{ maxLength: 120 }} InputLabelProps={{ shrink: true }} placeholder="Ex.: Lembrete da sessão de amanhã" />
                <TextField label="Texto da mensagem" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} fullWidth multiline minRows={7} maxRows={14}
                  inputRef={bodyRef} inputProps={{ maxLength: 2000 }} InputLabelProps={{ shrink: true }}
                  placeholder={"Olá {{responsavel}}, lembramos a sessão de {{paciente_primeiro_nome}} em {{data}} às {{hora_inicio}} com {{profissional}}. Confirme em {{link_checkin}}."}
                  helperText={`${form.body.length}/2000 · clique numa variável para inserir no ponto do cursor`} FormHelperTextProps={{ sx: { mx: 0 } }} />
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {variables.map((v) => (
                    <Tooltip key={v.key} title={`${v.label} · ex.: ${v.sample}`} arrow>
                      <Chip size="small" label={`{{${v.key}}}`} onClick={() => insertVariable(v.key)} sx={{ fontFamily: "monospace", fontSize: "0.75rem", bgcolor: colors.primaryLight, color: colors.primary, "&:hover": { bgcolor: colors.primarySoft || colors.primaryLight } }} />
                    </Tooltip>
                  ))}
                </Box>
                <TextField label="Template aprovado na Meta (opcional, para envio automático)" value={form.metaTemplateName} onChange={(e) => setForm({ ...form, metaTemplateName: e.target.value })} fullWidth size="small" inputProps={{ maxLength: 100 }} InputLabelProps={{ shrink: true }}
                  helperText="Só preencha quando o template existir na conta da Meta. O envio manual pelo link não precisa." FormHelperTextProps={{ sx: { mx: 0 } }} />
              </Box>
              <Box sx={{ background: colors.backgroundSubtle, border: `1px solid ${colors.borderLight}`, borderRadius: "8px", p: 2, minHeight: 200 }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 1, color: colors.textSecondary }}>Prévia com dados de exemplo</Typography>
                <Box sx={{ background: "#e7ffdb", borderRadius: "10px", p: 1.5, fontSize: "0.9rem", whiteSpace: "pre-wrap", overflowWrap: "anywhere", boxShadow: "0 1px 1px rgba(0,0,0,0.08)" }}>
                  {preview || <span style={{ color: colors.textMuted }}>Digite o texto para ver a prévia.</span>}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.5rem", gap: 1 }}>
          <Button onClick={() => setForm(null)} variant="outlined" disabled={saving} sx={{ textTransform: "none", color: colors.textSecondary, borderColor: colors.border }}>Cancelar</Button>
          <Button onClick={save} variant="contained" disabled={saving || !form?.name.trim() || !form?.body.trim()} sx={{ textTransform: "none", fontWeight: 600, backgroundColor: colors.primary, "&:hover": { bgcolor: colors.primaryHover }, boxShadow: "none" }}>
            {form?.id ? "Salvar" : "Incluir"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir mensagem"
        message={deleteTarget ? `Excluir "${deleteTarget.name}"? Se ela já foi usada em algum envio, será apenas inativada para preservar o histórico.` : ""}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={async () => { if (!deleteTarget) return; const t = deleteTarget; setDeleteTarget(null); await run(() => whatsappMessagesService.remove(t.id), "Mensagem excluída.", "Não foi possível excluir a mensagem."); }}
        onCancel={() => setDeleteTarget(null)}
      />

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default WhatsAppMessages;
